/**
 * Phase 42 — Map-Pin fallback picker.
 *
 * Used when text geocoding (Eircode → Google → ORS) returns no usable
 * coords. The manager drops a pin on a Google Map; we reverse-geocode
 * back to a formatted address and pass everything up. The result is
 * also written into the customer's `address_cache` server-side so the
 * next time this exact spot is needed, no Google call is made.
 *
 * The Maps JavaScript API key is fetched at mount time via
 * GET /api/maps/js-key (manager-authed), so it's never embedded in the
 * static bundle. Restrict the key with HTTP-referrer rules in Google
 * Cloud Console.
 */
import { useEffect, useState, useCallback } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Default to Ireland centroid (53.4, -8.0) when we have no tenant office.
const DEFAULT_CENTER = { lat: 53.4129, lng: -8.2439 };
const DEFAULT_ZOOM = 7;
const PIN_ZOOM = 16;

export default function MapPinPicker(props) {
  return props.open ? <MapPinPickerInner {...props} /> : null;
}

function MapPinPickerInner({
  open,
  onOpenChange,
  initialQuery = "",
  customerId = null,
  onPicked,
}) {
  const [mapsKey, setMapsKey] = useState("");
  const [keyLoaded, setKeyLoaded] = useState(false);
  const [pin, setPin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState("");

  // Load the JS API key (manager-authed) before mounting the loader. The
  // `useJsApiLoader` hook below can only be called ONCE per page-load with
  // a given options object — so we gate it behind the fetched key.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/maps/js-key");
        if (cancelled) return;
        if (!data?.configured || !data?.key) {
          toast.error("Map-Pin picker not configured (missing Google Maps key)");
          setKeyLoaded(true);
          return;
        }
        setMapsKey(data.key);
        setKeyLoaded(true);
      } catch {
        if (!cancelled) {
          toast.error("Could not load map");
          setKeyLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!keyLoaded) {
    // We must NOT mount `useJsApiLoader` before the key is fetched —
    // calling it twice with different options on the same page raises
    // "Loader must not be called again". Render a tiny placeholder.
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md p-6 text-center text-sm text-muted-foreground"
                       data-testid="map-pin-picker-dialog-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          <div className="mt-3">Loading Maps…</div>
        </DialogContent>
      </Dialog>
    );
  }
  if (!mapsKey) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md p-6 text-center text-sm"
                       data-testid="map-pin-picker-dialog-misconfigured">
          <div className="text-red-600 font-semibold">Maps API key not configured</div>
          <div className="text-xs text-muted-foreground mt-2">
            Set <span className="mono">GOOGLE_MAPS_API_KEY</span> on the backend and reload.
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return <MapPinPickerWithKey
            mapsKey={mapsKey}
            open={open}
            onOpenChange={onOpenChange}
            initialQuery={initialQuery}
            customerId={customerId}
            onPicked={onPicked}
            pin={pin} setPin={setPin}
            busy={busy} setBusy={setBusy}
            resolvedAddress={resolvedAddress}
            setResolvedAddress={setResolvedAddress}
         />;
}

function MapPinPickerWithKey({
  mapsKey, open, onOpenChange, initialQuery, customerId, onPicked,
  pin, setPin, busy, setBusy, resolvedAddress, setResolvedAddress,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "jobsbook-maps-loader",
    googleMapsApiKey: mapsKey,
    libraries: ["places"],
  });

  const onMapClick = useCallback(async (e) => {
    const next = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPin(next);
    setBusy(true);
    try {
      const { data } = await api.post("/maps/reverse-geocode", {
        ...next,
        customer_id: customerId || null,
      });
      setResolvedAddress(data?.formatted_address || "");
    } catch {
      setResolvedAddress("");
    } finally {
      setBusy(false);
    }
  }, [customerId]);

  const confirm = () => {
    if (!pin) return;
    onPicked?.({
      lat: pin.lat,
      lng: pin.lng,
      formatted_address: resolvedAddress || initialQuery || "",
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-2xl w-full max-h-[92vh] overflow-hidden p-0 gap-0"
        data-testid="map-pin-picker-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-accent" />
            Drop a pin
          </DialogTitle>
          <DialogDescription className="text-xs">
            We couldn't auto-locate <span className="font-semibold">{initialQuery || "this address"}</span>.
            Click on the map to set the exact location.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-[420px] sm:h-[480px]" data-testid="map-pin-canvas">
          {!isLoaded && !loadError && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading map…
            </div>
          )}
          {loadError && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-red-500 px-4 text-center">
              Could not load Google Maps. Check the key restrictions.
            </div>
          )}
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={pin || DEFAULT_CENTER}
              zoom={pin ? PIN_ZOOM : DEFAULT_ZOOM}
              onClick={onMapClick}
              options={{ disableDefaultUI: true, zoomControl: true, streetViewControl: false }}
            >
              {pin && <Marker position={pin} data-testid="map-pin-marker" />}
            </GoogleMap>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-secondary/30 text-xs space-y-1"
             data-testid="map-pin-summary">
          {pin ? (
            <>
              <div className="font-mono">
                Lat: {pin.lat.toFixed(6)} · Lng: {pin.lng.toFixed(6)}
              </div>
              <div className="text-muted-foreground">
                {busy ? "Resolving address…" : (resolvedAddress || "No address resolved — using coords only.")}
              </div>
            </>
          ) : (
            <div className="text-muted-foreground">Tap the map to drop a pin.</div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="map-pin-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!pin || busy}
            data-testid="map-pin-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Use this location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
